import { useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { DEPARTMENT_OPTIONS } from "../../data/site";

const SEMESTERS = ["Semester I", "Semester II", "Semester III", "Semester IV", "Semester V", "Semester VI", "Semester VII", "Semester VIII"];
const inputCls = "w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30";
const labelCls = "block text-xs uppercase tracking-widest text-forest/60 mb-1.5";
const empty = { semester: "Semester I", name: "", abc_id: "", department: "", year: "", phone: "", subjects: "" };

function parseCSV(text) {
  const rows = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return rows;
  const header = lines[0].toLowerCase();
  const start = header.includes("name") && header.includes("abc") ? 1 : 0;
  for (let i = start; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    if (!cols[1] && !cols[0]) continue;
    rows.push({
      semester: cols[0] || "Semester I",
      name: cols[1] || "",
      abc_id: cols[2] || "",
      department: cols[3] || "",
      year: cols[4] || "",
      subjects: (cols[5] || "").split(/[;|]/).map((s) => s.trim()).filter(Boolean),
    });
  }
  return rows;
}

export default function StudentRecordsManager() {
  const { records, subjects, reload } = useSite();
  const subjectOptions = (subjects || []).map((s) => s.name).filter(Boolean);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("All");
  const [deptFilter, setDeptFilter] = useState("All");
  const [yearFilter, setYearFilter] = useState("All");

  const toggleSubject = (name) => {
    const cur = String(form.subjects || "").split(",").map((s) => s.trim()).filter(Boolean);
    const next = cur.includes(name) ? cur.filter((s) => s !== name) : [...cur, name];
    setForm((f) => ({ ...f, subjects: next.join(", ") }));
  };

  const exportExcel = () => {
    if (!shown.length) { toast.error("No records to export"); return; }
    const aoa = [["Semester", "Name", "ABC ID", "Department", "Year", "Phone", "Subjects Offered"]];
    shown.forEach((r) => aoa.push([r.semester, r.name, r.abc_id, r.department, r.year, r.phone, (r.subjects || []).join("; ")]));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Student Records");
    XLSX.writeFile(wb, `student_records_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Excel downloaded");
  };

  const exportPdf = () => {
    if (!shown.length) { toast.error("No records to export"); return; }
    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
    const W = doc.internal.pageSize.getWidth();
    doc.setFontSize(14); doc.setFont("times", "bold");
    doc.text("Kanggui Christian College — Student Records", W / 2, 36, { align: "center" });
    autoTable(doc, {
      startY: 52,
      head: [["#", "Semester", "Name", "ABC ID", "Department", "Year", "Subjects Offered"]],
      body: shown.map((r, i) => [i + 1, r.semester, r.name, r.abc_id || "", r.department || "", r.year || "", (r.subjects || []).join(", ")]),
      styles: { fontSize: 8, cellPadding: 4 }, headStyles: { fillColor: [18, 58, 92] },
    });
    doc.save(`student_records_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF downloaded");
  };

  const years = ["All", ...Array.from(new Set(records.map((r) => r.year).filter(Boolean))).sort().reverse()];
  const shown = records.filter((r) =>
    (filter === "All" || r.semester === filter) &&
    (deptFilter === "All" || (r.department || "") === deptFilter) &&
    (yearFilter === "All" || (r.year || "") === yearFilter)
  );

  const openNew = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (r) => { setEditing(r.id); setForm({ semester: r.semester || "Semester I", name: r.name || "", abc_id: r.abc_id || "", department: r.department || "", year: r.year || "", phone: r.phone || "", subjects: (r.subjects || []).join(", ") }); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { semester: form.semester, name: form.name, abc_id: form.abc_id, department: form.department, year: form.year, phone: form.phone, subjects: String(form.subjects || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.update("records", editing, payload);
      else await api.create("records", payload);
      toast.success(editing ? "Record updated" : "Record added");
      setModal(false);
      await reload();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.remove("records", id); toast.success("Deleted"); await reload(); }
    catch { toast.error("Failed to delete"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) { toast.error("No valid rows found in CSV"); return; }
      await api.bulkRecords(rows);
      toast.success(`Imported ${rows.length} record(s)`);
      await reload();
    } catch { toast.error("Import failed"); }
    finally { e.target.value = ""; }
  };

  const onDownload = () => {
    const header = "semester,name,abc_id,department,year,subjects\n";
    const body = records.map((r) => [r.semester, r.name, r.abc_id, r.department, r.year, (r.subjects || []).join(";")].map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "student_records.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="records-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-serif text-2xl text-forest">{records.length} Student Record{records.length !== 1 ? "s" : ""}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <label data-testid="import-records-label" className="inline-flex items-center gap-2 border border-forest text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors cursor-pointer">
            <Upload size={14} /> Bulk Import
            <input data-testid="import-records-input" type="file" accept=".csv,text/csv" onChange={onImport} className="hidden" />
          </label>
          <button data-testid="download-records-btn" onClick={onDownload} className="inline-flex items-center gap-2 border border-forest text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors">
            <Download size={14} /> CSV
          </button>
          <button data-testid="records-export-excel" onClick={exportExcel} className="inline-flex items-center gap-2 border border-forest text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors">
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button data-testid="records-export-pdf" onClick={exportPdf} className="inline-flex items-center gap-2 border border-forest text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors">
            <FileText size={14} /> PDF
          </button>
          <button data-testid="add-record-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-forest px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
            <Plus size={14} /> Add Record
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {["All", ...SEMESTERS].map((s) => (
          <button key={s} data-testid={`records-filter-${s.replace(/\s/g, "-")}`} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === s ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>{s.replace("Semester ", "Sem ")}</button>
        ))}
      </div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select data-testid="records-dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className="bg-white border border-line focus:border-gold outline-none px-3 py-2 rounded-sm text-xs">
          <option value="All">All departments</option>
          {DEPARTMENT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select data-testid="records-year-filter" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="bg-white border border-line focus:border-gold outline-none px-3 py-2 rounded-sm text-xs">
          {years.map((y) => <option key={y} value={y}>{y === "All" ? "All years" : y}</option>)}
        </select>
        {(deptFilter !== "All" || yearFilter !== "All" || filter !== "All") && (
          <button onClick={() => { setFilter("All"); setDeptFilter("All"); setYearFilter("All"); }} className="text-xs text-muted underline hover:text-forest">Clear filters</button>
        )}
        <span className="text-xs text-muted ml-auto">{shown.length} shown</span>
      </div>

      <p className="text-xs text-muted mb-4">CSV columns: <span className="font-mono">semester, name, abc_id, department, year, subjects</span> (subjects separated by ; ). Registrations are auto-added here.</p>

      <div className="border border-line bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone text-forest/60 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Semester</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">ABC ID</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Department</th>
              <th className="text-left px-4 py-3">Year</th>
              <th className="text-left px-4 py-3">Subjects Offered</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {shown.map((r) => (
              <tr key={r.id} data-testid="record-row">
                <td className="px-4 py-3 text-muted">{r.semester}</td>
                <td className="px-4 py-3 font-medium text-forest">{r.name}</td>
                <td className="px-4 py-3 font-mono text-forest">{r.abc_id}</td>
                <td className="px-4 py-3 font-mono text-muted">{r.phone || "—"}</td>
                <td className="px-4 py-3 text-muted">{r.department || "—"}</td>
                <td className="px-4 py-3 text-muted">{r.year || "—"}</td>
                <td className="px-4 py-3 text-muted">{(r.subjects || []).join(", ") || <span className="text-gold/80 italic">add subjects</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button data-testid="edit-record-btn" onClick={() => openEdit(r)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors"><Pencil size={15} /></button>
                    <button data-testid="delete-record-btn" onClick={() => remove(r.id)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p className="p-8 text-muted">No records{filter !== "All" ? ` for ${filter}` : ""} yet. Add one or bulk-import a CSV.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <form data-testid="record-form" onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-3xl text-forest">{editing ? "Edit" : "Add"} Record</h3>
              <button type="button" onClick={() => setModal(false)} className="text-muted hover:text-forest"><X size={22} /></button>
            </div>
            <div className="mb-5">
              <label className={labelCls}>Semester</label>
              <select data-testid="record-field-semester" value={form.semester} onChange={(e) => setForm({ ...form, semester: e.target.value })} className={inputCls}>
                {SEMESTERS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mb-5">
              <label className={labelCls}>Name of Student</label>
              <input data-testid="record-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} />
            </div>
            <div className="mb-5">
              <label className={labelCls}>ABC ID</label>
              <input data-testid="record-field-abc" value={form.abc_id} onChange={(e) => setForm({ ...form, abc_id: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <label className={labelCls}>Department</label>
                <select data-testid="record-field-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className={inputCls}>
                  <option value="">Select</option>
                  {DEPARTMENT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Year</label>
                <input data-testid="record-field-year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={inputCls} placeholder="e.g. 2026" />
              </div>
            </div>
            <div className="mb-5">
              <label className={labelCls}>Phone Number</label>
              <input data-testid="record-field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="Student phone (used for record & ID card lookup)" />
            </div>
            <div className="mb-6">
              <label className={labelCls}>Subjects Offered</label>
              {subjectOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="subject-presets">
                  {subjectOptions.map((name) => {
                    const active = String(form.subjects || "").split(",").map((s) => s.trim()).includes(name);
                    return (
                      <button type="button" key={name} data-testid={`subject-chip-${name}`} onClick={() => toggleSubject(name)} className={`text-xs px-3 py-1 rounded-full border transition-colors ${active ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>
                        {active ? "✓ " : "+ "}{name}
                      </button>
                    );
                  })}
                </div>
              )}
              <input data-testid="record-field-subjects" value={form.subjects} onChange={(e) => setForm({ ...form, subjects: e.target.value })} className={inputCls} placeholder="Tap presets above or type, comma separated" />
            </div>
            <button data-testid="save-record-btn" type="submit" disabled={saving} className="w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update Record" : "Add Record"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
